/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Tooltip from 'react-tooltip-lite';
import { DragSource, DropTarget } from 'react-dnd';
import { at, flow, isEmpty } from 'lodash';
import moment from 'moment';
import { GoGear } from 'react-icons/go';
import * as Y from 'yjs';
import ItemTypes from '../../constants/itemTypes';
import modalTypes from '../../constants/modalTypes';
import Default from '../../constants/defaults';
import {
  WorkPackageStatuses,
  HigherLevelStatuses
} from '../../constants/storyStatusTypes';
import {
  resultCardId,
  hyphenate,
  truncate,
  workPackageStateCharacter
} from '../../utils/string';
import Store from '../../store/store';
import {
  toggleExpandedItem,
  expandItem,
  collapseItem
} from '../../actions/listActions';
import { openModal } from '../../actions/modalActions';
import {
  updateWorkPackage,
  updateWorkPackageStatus,
  updateWorkPackageLevel
} from '../../actions/workPackageActions';
import {
  startDrag,
  endDrag,
  setHoverBackground
} from '../../actions/notificationActions';
import MinusDelete from '../Buttons/minusDelete';
import ShortNameField from '../Common/shortNameField';
import VrsDropdown from './vrsDropdown';
import TypeDropdown from './typeDropdown';
import StateFilter from './stateFilter';
import StoryList from './storyList';
import PlanwallMenuContainer from './PlanwallMenuContainer';
import Table from './table';
import {
  extractImmediateDescendants,
  descendantsInTargetExist,
  extractChildrenOneLevelBelow
} from '../../utils/hierarchyHelpers';
import { allows } from '../../utils/authorization';
import { currentOrganization } from '../../utils/organizationHelpers';
import { filterChildrenResultsFromScope } from '../../selectors/workPackageSelector';

const resultSource = {
  beginDrag(props) {
    Store.dispatch(startDrag());
    props.resetLastStoryMoved();

    return {
      result: props.result,
      index: props.index,
      source: props.source,
      cadence: props.cadence
    };
  },
  canDrag(props) {
    return allows('update', props.result);
  },
  endDrag(props, monitor) {
    props.resetEmptyRow();
    Store.dispatch(endDrag());
    if (!props.hoverBackground) Store.dispatch(setHoverBackground(true));
    if (!monitor.didDrop()) {
      props.resetResults();
    }
  }
};

const collectSource = (connectTo, monitor) => ({
  connectDragSource: connectTo.dragSource(),
  connectDragPreview: connectTo.dragPreview(),
  isDragging: monitor.isDragging()
});

const resultTarget = {
  hover(props, monitor, component) {
    const dragItem = monitor.getItem();
    const { index: ownIndex, result } = props;
    if (dragItem.result.level !== result.level) return;
    const sameSource = dragItem.source === props.source;

    if (sameSource && dragItem.result.id !== result.id) {
      // What is my rectangle on screen?
      // eslint-disable-next-line
      const boundingRect = ReactDOM.findDOMNode(
        component
      ).getBoundingClientRect();
      // Where is my vertical middle?
      const ownMiddleY = boundingRect.top + boundingRect.height / 2;
      // Where is the mouse right now?
      const clientOffset = monitor.getClientOffset();
      const dragItemIndex = props.findRowIndex(dragItem.result.id);
      // Moving down: exit if we're in upper half
      if (dragItemIndex < ownIndex && clientOffset.y < ownMiddleY) {
        return;
      }
      // Moving up: exit if we're in lower half
      if (dragItemIndex > ownIndex && clientOffset.y > ownMiddleY) {
        return;
      }

      props.rerankTemp(dragItem.result, ownIndex);
      if (props.hoverBackground) Store.dispatch(setHoverBackground(false));
    }
  },
  drop(props, monitor) {
    const dragItem = monitor.getItem();
    const sameSource = dragItem.source === props.source;

    if (sameSource) {
      // props.rerank(dragItem);
      return dragItem;
    }
    props.addStory(dragItem);
    return dragItem;
  }
};

const collectTarget = (connector, monitor) => ({
  connectDropTarget: connector.dropTarget(),
  canDrop: monitor.canDrop()
});

// *Temporary* YDoc/text, used in StoryRow.summarize() function to help trim descriptions
// We keep these to avoid reinstantiating them every time
const ydoc = new Y.Doc();

const ytext = ydoc.getText();

class StoryRow extends React.Component {
  constructor(props) {
    super(props);

    this.storyHandle = undefined;

    this.state = {
      isEditable: false,
      highlight: false,
      result: props.result,
      originalBackgroundColor: undefined,
      visibleChildren: props.alwaysExpanded
    };

    this.textFieldRef = React.createRef();

    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.renderFieldColumn = this.renderFieldColumn.bind(this);
    this.highlightBriefly = this.highlightBriefly.bind(this);
    this.childrenData = this.childrenData.bind(this);
    this.expandStory = this.expandStory.bind(this);
    this.mainRow = this.mainRow.bind(this);
    this.miniRow = this.miniRow.bind(this);
    this.emptyRow = this.emptyRow.bind(this);
    this.toggleDropDownMenu = this.toggleDropDownMenu.bind(this);
    this.changeResultStatus = this.changeResultStatus.bind(this);
    this.changeResultType = this.changeResultType.bind(this);
  }

  componentDidMount() {
    if (this.storyHandle) {
      this.setState({
        originalBackgroundColor: this.storyHandle.style.background
      });
    }
  }

  componentWillReceiveProps(nextProps) {
    this.setState({ result: nextProps.result });
  }

  isExpanded = () => {
    const { expandedItems, result } = this.props;
    return expandedItems.find((i) => i.id === result.id);
  };

  openDetailCard = (level) => {
    const { result } = this.props;
    switch (level) {
      case 1:
        return Store.dispatch(
          openModal(modalTypes.resultDetail, {
            id: result.id,
            workPackage: result
          })
        );
      case 2:
        return Store.dispatch(
          openModal(modalTypes.featureDetail, { id: result.id })
        );
      case 3:
        return Store.dispatch(
          openModal(modalTypes.capabilityDetail, { id: result.id })
        );
      case 4:
        return Store.dispatch(
          openModal(modalTypes.epicDetail, { id: result.id })
        );
      default:
        return null;
    }
  };

  generateLevelsDeep = () => {
    const { levelsDeep } = this.props;

    if (!levelsDeep) {
      return 'one-level-deep';
    }
    if (levelsDeep === 'one-level-deep') {
      return 'two-levels-deep';
    }
    if (levelsDeep === 'two-levels-deep') {
      return 'three-levels-deep';
    }
    return '';
  };

  storyChildren = (highlightedIds) => {
    const {
      result,
      results,
      source,
      parentId,
      mini,
      listWidth,
      listHeight,
      virtualized,
      parentVisibleFields,
      fieldWidths,
      alwaysExpanded,
      dropDown,
      setDropdown,
      target
    } = this.props;
    const { level } = result;
    const { visibleChildren } = this.state;

    if (this.isExpandable() && (this.isExpanded() || alwaysExpanded)) {
      const childrenLevel = level - 1;
      const columnFields = Default.tableFields[childrenLevel].fields;
      const fields = parentVisibleFields.map((field) => {
        const childrenField = columnFields.find(
          (f) => f.name === field.name || f.under === field.name
        );
        if (childrenField) {
          return childrenField;
        }
        return {
          name: 'empty',
          under: field.name,
          minWidth: field.minWidth,
          maxWidth: field.maxWidth
        };
      });

      const scope = results.filter((r) => r.level <= childrenLevel);
      const topLevelPackages = this.childrenData();
      const topLevelPackageIDs = topLevelPackages.map((child) => child.id);
      let allData;

      if (source.includes('team-panel')) {
        allData = topLevelPackages;
      } else {
        const descendants = extractImmediateDescendants(
          topLevelPackageIDs,
          scope,
          childrenLevel
        );
        allData = [...topLevelPackages, ...descendants];
      }

      if (!mini && source !== 'SideTargetDetail') {
        return (
          <Table
            results={allData}
            visibleFields={fields}
            fieldWidths={fieldWidths}
            source={source}
            level={childrenLevel}
            tableFields={columnFields}
            delete={this.props.delete}
            virtualized={source === 'PlanWallTable' ? false : virtualized}
            parentResult={result}
            dropDown={dropDown}
            setDropdown={setDropdown}
            expanded
            alwaysExpanded={alwaysExpanded}
            isChildren
            levelsDeep={this.generateLevelsDeep()}
            listWidth={listWidth}
            target={target}
            highlightedIds={highlightedIds}
          />
        );
      }

      return (
        <div
          className={`list-header-informative ${visibleChildren ? '' : 'hide'}`}
        >
          <StoryList
            delete={this.props.delete}
            level={childrenLevel}
            results={allData}
            source={source}
            fields={parentVisibleFields}
            uiParent={result}
            parentId={parentId}
            mini={mini}
            target={target}
            listWidth={listWidth}
            listHeight={listHeight}
            virtualized={source === 'PlanWallTable' ? false : virtualized}
            expandedItems={this.props.expandedItems}
            alwaysExpanded={alwaysExpanded}
            dropDown={dropDown}
            setDropdown={setDropdown}
          />
        </div>
      );
    }
    return null;
  };

  isExpandable = () => {
    const { result, level, source } = this.props;
    const itemLevel = this.isTargetDetailCard() ? result.level : level;

    const childrenData = this.childrenData();

    return itemLevel > 1 && !!childrenData.length && source !== 'ProductRow';
  };

  isTargetDetailCard = () => {
    const { source } = this.props;
    return source === 'TargetDetail' || source === 'SideTargetDetail';
  };

  storyRowClassName = () => {
    const {
      uiParent,
      result,
      source,
      mini,
      lastStoryMoved,
      highlightRow
    } = this.props;

    let className = 'story-row';
    className = this.state.highlight
      ? className.concat(' highlight')
      : className;

    className = result.blocked ? className.concat(' highlight-red') : className;

    if (this.isTargetDetailCard() && result.target) {
      className = className.concat(' story-row-grayed-out');
    }

    if (isEmpty(uiParent) || mini || source === 'SideTargetDetail') {
      className = className.concat(' story-row-parent');
    }

    if (this.isExpanded()) {
      className = className.concat(' story-row-expanded');
    }

    if (lastStoryMoved === result.id && !this.state.removeHilight) {
      className = className.concat(' story-row-highlighted');
      // set a timer to remove this from className after 25 seconds
      setTimeout(() => {
        this.setState({ removeHilight: true });
      }, 27000);
    }

    if (highlightRow) {
      className = className.concat(' no-fade-highlight-story');
    } else {
      className = className.concat(' story-row-no-highlight');
    }

    if (mini) {
      if (isEmpty(uiParent)) {
        className = className.concat(' story-row-second-child');
      } else {
        switch (result.level) {
          case 1:
            className = className.concat(' story-row-third-child');
            break;
          case 2:
            className = className.concat(' story-row-second-child');
            break;
          case 3:
            className = className.concat(' story-row-first-child');
            break;
          default:
        }
      }
    }

    return className;
  };

  isParentInTarget = () => {
    const { result, results, parentId, target } = this.props;
    if (!target) return false;
    const resultParentId = result.parent_id;
    const parent = results.find(
      (workPackage) => workPackage.id === resultParentId
    );

    if (parentId === 0) {
      return target.workPackageIds.includes(resultParentId);
    }
    return parent && parent.target_id === parentId;
  };

  toggleShortNameEditField = (allowEdit) => {
    if (allowEdit)
      this.setState((prevState) => ({ isEditable: !prevState.isEditable }));
  };

  childrenData() {
    const { result, results, source, parentId } = this.props;
    let { children } = this.props;

    if (source.includes('team-panel')) {
      if (source.includes('ownerless')) {
        children = extractChildrenOneLevelBelow(result, true, true);
      }

      if (source.includes('member')) {
        children = extractChildrenOneLevelBelow(
          result,
          true,
          false,
          parentId // parentId is the user id to filter workpackages by
        );
      }
    }

    if (source === 'SideTargetDetail') {
      if (parentId > 0) {
        return children.filter((child) => child.target_id === parentId);
      }
    }

    return children;
  }

  hasChildren(level) {
    const subLevel = Default.heirarchy.findIndex((el) => el === level) - 1;
    return subLevel >= 0;
  }

  highlightBriefly() {
    this.setState({ highlight: true }, () => {
      setTimeout(() => this.setState({ highlight: false }), 700);
    });
  }

  summarize(original) {
    if (!original) return '';

    const max_length = 650; // Characters, after whitespace consolidation.

    const cut_and_clean = (text) => {
      // Cut to length -- we cut *before* collapsing whitespace so the regexp below doesn't run on a potential 10MB of text
      if (text.length > max_length) text = `${text.substr(0, max_length)}...`;

      // Convert and consolidate any tabs and newlines to spaces
      return text.replaceAll(/\s+/g, ' ');
    };

    try {
      original = JSON.parse(original);
    } catch (e) {
      // If we couldn't parse the text as JSON, it means it's not a YDoc, consider it plaintext
      return cut_and_clean(original);
    }

    ytext.delete(0, ytext.length);

    ytext.applyDelta(original);

    const plaintext = ytext.toString();

    return cut_and_clean(plaintext);
  }

  calculateWsjf() {
    let sum = '-';
    if (this.props.result.result_type) {
      const {
        value = 0,
        rroe = 0,
        tc = 0,
        size = 0
      } = this.props.result.result_type.type_attributes;
      if (size > 0) {
        sum =
          (parseInt(value, 10) + parseInt(rroe, 10) + parseInt(tc, 10)) /
          parseInt(size, 10);
      }
    }
    return sum;
  }

  expandStory() {
    this.props.toggleExpandedItem(this.props.result);
  }

  changeResultStatus(result, status) {
    Store.dispatch(updateWorkPackageStatus(result.id, status, result.state));
  }

  toggleDropDownMenu(e, type, props) {
    e.stopPropagation();
    const { result, organizationLevels, setDropdown } = this.props;
    let { dropDown = {} } = this.props;
    if (dropDown.id) {
      dropDown = {};
    } else {
      const newProps = {
        ...props,
        organizationLevels
      };
      const { target: element } = e;
      const classes = 'planwall-drop-menu';
      dropDown = {
        ...newProps,
        result,
        type,
        classes,
        elementClicked: element,
        elementAttached: element.closest('.dropdown'),
        parent:
          element.closest('#planwall-middle-component') ||
          element.closest('.middle'),
        id: result.id,
        closeDropdown: () => {
          setDropdown({ dropDown: {} });
          document.querySelector('body').style.overflow = 'scroll';
        },
        index: props.index,
        level: this.props.level
      };
    }
    setDropdown({ ...dropDown });
  }

  mainRow() {
    const {
      result,
      parentVisibleFields,
      level,
      source,
      connectDragSource,
      rowHeight,
      hoverBackground,
      isChildren
    } = this.props;
    const { originalBackgroundColor } = this.state;
    const isExpandable = this.isExpandable();
    const columnFields = Default.tableFields[level].fields;
    const visibleFields = parentVisibleFields
      .filter((p) => p.name !== 'Rank')
      .map((field) => {
        const childrenField = columnFields.find(
          (f) => f.name === field.name || f.under === field.name
        );
        if (childrenField) {
          return childrenField;
        }
        return field;
      });
    const leftVisibleFields = visibleFields.filter((field) => field.groupLeft);
    const rightVisibleFields = visibleFields.filter(
      (field) => !field.groupLeft
    );

    const allowEdit = allows('update', result);
    const deletedClass = result.deleted_at ? 'deleted' : '';

    const dragClass = allowEdit ? 'move-cursor' : '';

    const caret = this.isExpanded()
      ? 'glyphicon-triangle-bottom'
      : 'glyphicon-triangle-right';

    let divMappedFields = (
      <div className="mapped-fields">
        <div className={`left-field-group ${dragClass}`}>
          {leftVisibleFields.map((field) => {
            if (field.name === 'empty') {
              return (
                <div
                  key={field.under}
                  className={`${field.under.toLowerCase()}-col`}
                >
                  &nbsp;
                </div>
              );
            }
            return (
              <div
                key={field.name}
                className={`${field.name.toLowerCase()}-col`}
              >
                {this.renderFieldColumn(field)}
              </div>
            );
          })}
        </div>

        <div className="right-field-group">
          {rightVisibleFields.map((field) => {
            if (field.name === 'empty') {
              return (
                <div
                  key={field.under}
                  className={`${field.under.toLowerCase()}-col`}
                >
                  &nbsp;
                </div>
              );
            }
            return (
              <div
                key={field.name}
                className={`${field.name.toLowerCase().replace(' ', '-')}-col`}
              >
                {this.renderFieldColumn(field)}
              </div>
            );
          })}
        </div>
        {level === 5 && (
          <div className="wall-flip-container">
            <a
              href={`${window.location.origin}/planwall/o/${result.id}/teams/1`}
              className="btn btn-link wall-flip"
            />
          </div>
        )}
      </div>
    );

    divMappedFields = connectDragSource(divMappedFields);

    const matchDropdownToButton =
      this.props.dropDown &&
      this.props.dropDown.index === this.props.index &&
      this.props.dropDown.type === 'cogMenu' &&
      this.props.dropDown.level === this.props.level;

    return this.props.connectDragPreview(
      <div
        ref={(elem) => {
          this.storyHandle = elem;
        }}
        className={`handle ${deletedClass}`}
        style={{
          height: `${rowHeight}px`,
          backgroundColor: !hoverBackground ? '#EEEEEE' : undefined
        }}
        onMouseOver={() => {
          if (hoverBackground) {
            this.storyHandle.classList.add('hovered');
            // this.storyHandle.style.background = '#f1f1f1';
            // this.textFieldRef.current
            //   ? (this.textFieldRef.current.style.background = '#f1f1f1')
            //   : null;
          }
        }}
        onFocus={() => {
          if (hoverBackground && this.storyHandle) {
            this.storyHandle.classList.add('focused');
            // this.storyHandle.style.background = '#C2C2C2';
            // this.textFieldRef.current
            //   ? (this.textFieldRef.current.style.background = 'transparent')
            //   : null;
          }
        }}
        onMouseOut={() => {
          this.storyHandle.classList.remove('hovered');

          // this.storyHandle.style.background = originalBackgroundColor;
          // this.textFieldRef.current
          //   ? (this.textFieldRef.current.style.background = originalBackgroundColor)
          //   : null;
        }}
        onBlur={() => {
          this.storyHandle.classList.remove('focused');
          // this.storyHandle.style.background = originalBackgroundColor;
          // this.textFieldRef.current
          //   ? (this.textFieldRef.current.style.background = originalBackgroundColor)
          //   : null;
        }}
      >
        <div className="btn-component-div opener left">
          {isExpandable && (
            <button
              type="button"
              className="btn btn-link"
              onClick={this.expandStory}
            >
              <span className={`glyphicon ${caret}`} aria-hidden="true" />
            </button>
          )}
        </div>
        {source === 'SideTargetDetail' ? (
          <div className="delete-container">
            <MinusDelete
              handleClick={() => this.props.removeStory(this.props.index)}
            />
          </div>
        ) : (
          <div
            style={{ position: 'relative' /* needed for dropdown to work */ }}
          >
            <div className="row-menu">
              <div className="dropdown btn-component-div">
                <button
                  id="detail-dropdown"
                  type="button"
                  className="btn btn-link"
                  onClick={(e) =>
                    this.toggleDropDownMenu(e, 'cogMenu', {
                      index: this.props.index
                    })
                  }
                  disabled={!allowEdit}
                >
                  <GoGear size="1em" color="black" />
                </button>
              </div>
            </div>
            {matchDropdownToButton && (
              <PlanwallMenuContainer
                dropDown={this.props.dropDown}
                fieldName="cogMenu"
                type="cogMenu"
                isChildren={isChildren}
                source={source}
              />
            )}
          </div>
        )}
        {divMappedFields}
      </div>
    );
  }

  emptyRow() {
    const { mini, rowHeight } = this.props;

    if (mini) return <div className="row cadence-result" />;

    return (
      <div
        className="story-row-grayed-out will-drop"
        style={{ height: `${rowHeight - 1}px`, width: '100%' }}
      >
        <div className="handle handle-not-expanded" style={{ borderLeft: 0 }}>
          <div className="row-menu">&nbsp;</div>
          <div className="mapped-fields col-md-12 empty-row">&nbsp;</div>
        </div>
      </div>
    );
  }

  toggleVisibility = () => {
    const newVisibleChildren = !this.state.visibleChildren;
    this.setState((prevState) => ({
      ...prevState,
      visibleChildren: newVisibleChildren
    }));
    if (newVisibleChildren) this.props.expandItem(this.props.result);
    else this.props.collapseItem(this.props.result);
  };

  displayChevron = (isCadenceStory, showChevron) => {
    if (isCadenceStory) return null;
    if (!showChevron) return null;

    const caret = this.state.visibleChildren
      ? 'glyphicon-triangle-bottom'
      : 'glyphicon-triangle-right';

    return (
      <span
        onClick={this.toggleVisibility}
        className={`glyphicon ${caret}`}
        aria-hidden="true"
      />
    );
  };

  miniRow() {
    const {
      result,
      connectDragSource,
      isCadenceStory,
      leftList,
      targetRow,
      source,
      results,
      showChildren
    } = this.props;
    const allowEdit = allows('update', result);

    const dragClass = allowEdit ? 'move-cursor' : '';
    const highlightClass = this.state.highlight ? 'highlight' : '';
    const deletedClass = result.deleted_at ? 'deleted' : '';
    const children = this.childrenData();

    let showChevron =
      result.has_children &&
      showChildren &&
      !leftList &&
      children.length &&
      source !== 'ProductRow';

    if (['SideTargetDetail', 'SideTargetList'].includes(source)) {
      showChevron =
        descendantsInTargetExist(result, results) && result.has_children;
    }

    const row = (
      <div
        key={result.id}
        className={`row cadence-result ${dragClass} ${highlightClass} ${deletedClass}`}
      >
        <div className="result-card-id">
          {this.displayChevron(isCadenceStory, showChevron)}
          <a onClick={() => this.openDetailCard(result.level)}>
            {resultCardId(result)}
          </a>
        </div>
        <div
          className="name truncated-text"
          style={{ paddingLeft: '3px', width: showChevron ? '90%' : '92%' }}
        >
          <span className={result.blocked ? 'red' : undefined}>
            {result.name}
          </span>
        </div>
        <div
          className="right"
          style={{
            display: 'flex',
            paddingRight: '3px',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          {allowEdit && (
            <button
              type="button"
              className="btn-close remove-story-from-list"
              onClick={() => this.props.removeStory(this.props.result)}
            >
              &times;
            </button>
          )}
          <span className={`icon ${hyphenate(result.state || '')}`}>
            {workPackageStateCharacter(result)}
          </span>
        </div>
      </div>
    );

    return connectDragSource(row);
  }

  handleFieldChange(field) {
    const { result } = this.state;
    let data = { ...field };

    if (field.short_name && field.short_name === result.name) return;
    if (field.short_name) result.name = field.short_name;
    if (field.type_attributes) {
      result.result_type.type_attributes = {
        ...result.result_type.type_attributes,
        ...field.type_attributes
      };
      data = { type_attributes: result.result_type.type_attributes };
    }
    if (field.target) data = { [field.target.name]: field.target.value };
    Store.dispatch(updateWorkPackage(this.props.result.id, data));
    this.setState((prevState) => ({
      result: { ...prevState.result, ...data }
    }));
  }

  changeResultType(attributes) {
    const { organizationLevels, result } = this.props;
    const { organizationLevelId } = attributes;
    const organizationLevel = organizationLevels.find(
      (org) => org.id === Number(organizationLevelId)
    );
    if (organizationLevel) {
      Store.dispatch(
        updateWorkPackageLevel(
          result,
          organizationLevel.level,
          organizationLevel.id
        )
      );
    }
  }

  wrapTextInsideTooltip = (text) => {
    if (text.length > 10)
      return (
        <Tooltip
          content={<div className="custome-tooltip"> {text}</div>}
          direction="up"
        >
          <div> {truncate(text, 10)} </div>
        </Tooltip>
      );
    return <div> {truncate(text, 10)} </div>;
  };

  renderFieldColumn(field, item) {
    const {
      workPackages,
      organizationLevels,
      users = [],
      targets = [],
      currentTeams,
      isChildren,
      products,
      source
    } = this.props;
    const fieldName = field.name.toLowerCase();
    const result = item || this.state.result;
    const organizationLevel =
      organizationLevels.find(
        (orgLevel) => orgLevel.id === result.organization_level_id
      ) || {};
    const organizationLevelOptions = organizationLevels.reduce(
      (options, orgLevel) => {
        const newOptions = options;
        if (organizationLevel.id !== orgLevel.id && !orgLevel.deleted_at)
          newOptions[orgLevel.id] = orgLevel.name;
        return newOptions;
      },
      {}
    );
    const defaultType = ['rroe', 'tc'].includes(fieldName) ? 'risk' : fieldName;
    const { defaultOptions } = Store.getState().defaultOptionsReducer;
    let defaults = defaultOptions[`${defaultType}_defaults`];
    const resultType = result.result_type || {};
    const stateOptions =
      result.level === 1 ? WorkPackageStatuses : HigherLevelStatuses;

    if (fieldName === 'size') {
      if (result.level > 1) {
        defaults = defaultOptions.t_shirt_size_defaults;
      } else {
        defaults = defaultOptions.size_defaults;
      }
    }
    if (
      fieldName === 'value' &&
      result.level === 1 &&
      resultType &&
      resultType.type === 'Defect'
    )
      defaults = defaultOptions.severity_defaults;

    let attributes = resultType ? resultType.type_attributes : {};

    if (typeof attributes === 'string') attributes = JSON.parse(attributes);
    const allowEdit = allows('update', result);
    const modalType = {
      1: modalTypes.resultDetail,
      2: modalTypes.featureDetail,
      3: modalTypes.capabilityDetail,
      4: modalTypes.epicDetail,
      5: null
    };
    const sprintToShow = this.props.cadenceToShow;
    const teamToShow =
      currentTeams &&
      currentTeams.filter((team) => team.id === result.team_id)[0];
    const parent =
      result.parent_id && workPackages
        ? workPackages.find((w) => w.id === result.parent_id)
        : null;
    const createdBy = users.find((u) => u.id === result.creator_id) || {};
    const owner = users.find((u) => u.id === result.owner_id) || {};
    const target = targets.find((t) => t.id === result.target_id) || {};

    const matchDropdownToButton =
      this.props.dropDown &&
      this.props.dropDown.index === this.props.index &&
      this.props.dropDown.field === fieldName &&
      this.props.dropDown.level === this.props.level;

    switch (fieldName) {
      case 'rank':
        return null;
      case 'id':
        return (
          <a
            onClick={() =>
              Store.dispatch(
                openModal(modalType[result.level || 1], { id: result.id })
              )
            }
          >
            <strong>
              {result.result_type ? resultCardId(result) : `O${result.id}`}
            </strong>
          </a>
        );
      case 'title':
        return (
          <div title={result.name} className="story-row-name name">
            <ShortNameField
              shortName={result.name}
              onNameChange={this.handleFieldChange}
              allowEdit={allowEdit}
              ref={this.textFieldRef}
              storyRow
              rowHeight={this.props.rowHeight}
            />
          </div>
        );
      case 'value':
      case 'risk':
      case 'size':
      case 'tc':
      case 'rroe':
        return (
          <div
            style={{ position: 'relative' /* needed for dropdown to work */ }}
          >
            <VrsDropdown
              type={fieldName}
              level={result.level}
              data={attributes}
              options={defaults ? defaults.values : {}}
              disabled={!allowEdit}
              handleSelect={this.handleFieldChange}
              toggleDropDownMenu={this.toggleDropDownMenu}
              rowIndex={this.props.index}
              resultType={this.props.result.result_type}
            />
            {matchDropdownToButton && (
              <PlanwallMenuContainer
                isChildren={isChildren}
                dropDown={this.props.dropDown}
                fieldName={fieldName}
                type="vrsMenu"
                source={source}
              />
            )}
          </div>
        );
      case 'wsjf':
        return (
          <div className="btn-component-div">
            <button type="button" className="no-click icon wsjf icon">
              {result.wsjf === null ? '-' : result.wsjf}
            </button>
          </div>
        );
      case 'state':
        return (
          <div
            style={{ position: 'relative' /* needed for dropdown to work */ }}
          >
            <StateFilter
              level={result.level}
              type="changeStatus"
              changeStatus={this.changeResultStatus}
              result={result}
              toggleDropDownMenu={this.toggleDropDownMenu}
              disabled={!allowEdit}
              options={stateOptions}
              rowIndex={this.props.index}
              fieldName={fieldName}
            />
            {matchDropdownToButton && (
              <PlanwallMenuContainer
                dropDown={this.props.dropDown}
                fieldName={fieldName}
                type="stateMenu"
                isChildren={isChildren}
                source={source}
              />
            )}
          </div>
        );
      case 'description':
        return <div>{this.summarize(at(result, [field.source])[0])}</div>;
      case 'sprint':
        return this.wrapTextInsideTooltip(
          sprintToShow ? sprintToShow.short_name : ''
        );
      case 'target':
        return this.wrapTextInsideTooltip(
          !isEmpty(target) ? target.short_name : ''
        );
      case 'parent':
        return (
          <div>
            {!!parent && (
              <Tooltip
                content={
                  <div className="custome-tooltip"> {parent.short_name}</div>
                }
              >
                <a
                  onClick={() =>
                    Store.dispatch(
                      openModal(modalType[parent.level || 1], { id: parent.id })
                    )
                  }
                >
                  {parent.result_type ? resultCardId(parent) : `O${parent.id}`}
                </a>
              </Tooltip>
            )}
          </div>
        );
      case 'blocked':
        return <div>{result.blocked ? 'Yes' : 'No'}</div>;
      case 'date added':
        return <div>{moment(result.created_at).format('YYYY/MM/DD')}</div>;
      case 'closed date':
        return (
          <div>
            {resultType.type === 'Defect' && result.accepted_at
              ? moment(result.accepted_at).format('YYYY/MM/DD')
              : ''}
          </div>
        );
      case 'created by':
        return (
          <div>
            {this.wrapTextInsideTooltip(
              !isEmpty(createdBy)
                ? createdBy.nickname || createdBy.name
                : 'Unknown'
            )}
          </div>
        );
      case 'owner':
        return (
          <div>
            {this.wrapTextInsideTooltip(
              !isEmpty(owner) ? owner.nickname || owner.name : ''
            )}
          </div>
        );
      case 'team':
        return this.wrapTextInsideTooltip(
          teamToShow ? teamToShow.short_name : ''
        );
      case 'type':
        return (
          <div
            style={{ position: 'relative' /* needed for dropdown to work */ }}
          >
            <TypeDropdown
              type={fieldName}
              data={resultType.kind || ''}
              options={organizationLevelOptions}
              disabled={!allowEdit}
              handleSelect={this.changeResultType}
              rowIndex={this.props.index}
              toggleDropDownMenu={this.toggleDropDownMenu}
            />
            {matchDropdownToButton && (
              <PlanwallMenuContainer
                dropDown={this.props.dropDown}
                fieldName={fieldName}
                type="typeMenu"
                source={source}
              />
            )}
          </div>
        );
      case 'product':
        const product =
          !isEmpty(products) &&
          products.find((p) => p.id === result[field.source]);

        return (
          <span>{this.wrapTextInsideTooltip(product?.short_name || '')}</span>
        );

      default:
        return <div>{at(result, [field.source])[0]}</div>;
    }
  }

  render() {
    const {
      mini,
      uiParent,
      result,
      source,
      connectDropTarget,
      isEmpty,
      showTrash,
      sortField,
      showChildren,
      highlightedIds
    } = this.props;

    if (
      (mini || source === 'SideTargetDetail') &&
      this.isParentInTarget() &&
      uiParent.id !== result.parent_id
    )
      return null;

    if (result.deleted_at && !showTrash) return null;

    if (isEmpty) {
      return <div className={this.storyRowClassName()}>{this.emptyRow()}</div>;
    }

    // Little row or big row? :)
    let row = mini ? this.miniRow() : this.mainRow();

    // Disable DnD if there's more than one product selected or it's not sorted
    // by rank
    row =
      (source && source.indexOf('left-') > -1) ||
      (source && source.indexOf('target') > -1) ||
      source === 'SideTargetDetail' ||
      sortField !== 'rank'
        ? row
        : connectDropTarget(row);

    return (
      <>
        <div className={this.storyRowClassName()}>
          {row}
          {showChildren && this.storyChildren(highlightedIds)}
        </div>
      </>
    );
  }
}

StoryRow.propTypes = {
  result: PropTypes.object,
  highlightedIds: PropTypes.array,
  parentVisibleFields: PropTypes.arrayOf(PropTypes.any),
  fieldWidths: PropTypes.object,
  source: PropTypes.string,
  index: PropTypes.number,
  level: PropTypes.number,
  cadence: PropTypes.number,
  lastStoryMoved: PropTypes.number,
  mini: PropTypes.bool,
  alwaysExpanded: PropTypes.bool,
  hoverBackground: PropTypes.bool,
  rowHeight: PropTypes.number,
  organizationLevels: PropTypes.array,
  rerankTemp: PropTypes.func,
  addStory: PropTypes.func,
  removeStory: PropTypes.func,
  updateWorkPackageStatus: PropTypes.func,
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  dropDown: PropTypes.object,
  setDropdown: PropTypes.func,
  showChildren: PropTypes.bool // should I show the children of this result in a sublist?
};

StoryRow.defaultProps = {
  level: 1,
  parentVisibleFields: [],
  alwaysExpanded: false,
  organizationLevels: [],
  showChildren: true
};

const mapStateToProps = (state, ownProps) => {
  const { result } = ownProps;
  const cadenceIds = result ? result.cadence_ids : [];
  const cadences = state.cadenceReducer.cadences.filter(
    (cadence) => cadenceIds.indexOf(cadence.id) !== -1
  );

  const organizationLevels = currentOrganization().organization_levels;

  if (!cadences || cadences.length === 0) {
    return {
      organizationLevels,
      workPackages: state.workPackageReducer.all,
      expandedItems: state.listReducer.expandedItems,
      users: state.userReducer.users,
      targets: state.targetReducer.targets,
      currentTeams: state.defaultOptionsReducer.currentTeams,
      currentProducts: state.defaultOptionsReducer.currentProducts,
      products: state.productReducer.products,
      all: state.workPackageReducer.all,
      children: filterChildrenResultsFromScope(state, ownProps)
    };
  }

  cadences.sort((a, b) => b.id - a.id);
  const cadenceToShow = result
    ? cadences.find((c) => {
        const cadenceId = result.current_cadence
          ? result.current_cadence.id
          : result.current_cadence_id;
        return c.id === cadenceId;
      })
    : cadences[0];

  return {
    organizationLevels,
    cadenceToShow,
    workPackages: state.workPackageReducer.all,
    expandedItems: state.listReducer.expandedItems,
    users: state.userReducer.users,
    targets: state.targetReducer.targets,
    currentTeams: state.defaultOptionsReducer.currentTeams,
    currentProducts: state.defaultOptionsReducer.currentProducts,
    products: state.productReducer.products,
    all: state.workPackageReducer.all,
    children: filterChildrenResultsFromScope(state, ownProps)
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      expandItem,
      collapseItem,
      toggleExpandedItem
    },
    dispatch
  );
export default flow([
  DragSource(ItemTypes.RESULT, resultSource, collectSource),
  DropTarget(ItemTypes.RESULT, resultTarget, collectTarget)
])(connect(mapStateToProps, mapDispatchToProps)(StoryRow));
